import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useEffect, useState } from "react";
import { toast, Bounce } from "react-toastify";
import { useTranslate } from "../../hooks/LanguageProvider";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface Props {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  oem: any;
}

const EMPTY_OEM = {
  id_oem: "",
  id_user: "",
  company_name: "",
  industry_sector: "",
  country: "",
  city: "",
  headquarters: "",
  address: "",
  email: "",
  contact_email: "",
  phone: "",
  website: "",
  industrial_status: "active",
};

const numberOnlyFields = ["phone"] as const;

export default function EditOEMModal({
  show,
  onHide,
  onSuccess,
  oem: oemProp,
}: Props) {
  const { translate } = useTranslate();
  const [oem, setOEM] = useState(EMPTY_OEM);

  useEffect(() => {
    if (!show) return;

    if (oemProp) {
      setOEM({
        id_oem: String(oemProp.id_oem || oemProp.id || ""),
        id_user: String(oemProp.id_user || oemProp.user_id || ""),
        company_name: oemProp.company_name || "",
        industry_sector: oemProp.industry_sector || "",
        country: oemProp.country || "",
        city: oemProp.city || "",
        headquarters: oemProp.headquarters || "",
        address: oemProp.address || "",
        email: oemProp.email || oemProp.contact_email || "",
        contact_email: oemProp.contact_email || "",
        phone: String(oemProp.phone || ""),
        website: oemProp.website || "",
        industrial_status: String(
          oemProp.industrial_status || oemProp.status || "active",
        ).toLowerCase(),
      });
    } else {
      setOEM(EMPTY_OEM);
    }
  }, [show, oemProp?.id_oem, oemProp?.id]);

  const sanitizeNumber = (value: string) => value.replace(/\D/g, "");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    setOEM((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    setOEM(EMPTY_OEM);
    onHide();
  };

  const handleUpdate = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/oems/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...oem,
          id: Number(oem.id_oem),
          user_id: Number(oem.id_user),
          industrial_status: oem.industrial_status,
          phone: oem.phone,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        toast.error(`${translate("error")}: ${errorText}`, {
          transition: Bounce,
        });
        return;
      }

      toast.success(translate("oem_updated") || "OEM updated successfully", {
        transition: Bounce,
      });

      handleClose();
      onSuccess();
    } catch (error) {
      console.error(error);
      toast.error(translate("error_updating_oem") || "Error updating OEM", {
        transition: Bounce,
      });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{translate("edit_oem") || "Edit OEM"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form autoComplete="off">
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {translate("company_name") || "Company Name"}
                </Form.Label>
                <Form.Control
                  name="company_name"
                  value={oem.company_name}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{translate("industry") || "Industry"}</Form.Label>
                <Form.Control
                  name="industry_sector"
                  value={oem.industry_sector}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{translate("email")}</Form.Label>
                <Form.Control
                  name="email"
                  type="email"
                  value={oem.email}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{translate("phone")}</Form.Label>
                <Form.Control
                  name="phone"
                  value={oem.phone}
                  onChange={(e) =>
                    setOEM((prev) => ({
                      ...prev,
                      phone: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  inputMode="numeric"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{translate("status")}</Form.Label>
                <select
                  className="form-select"
                  name="industrial_status"
                  value={oem.industrial_status || "active"}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    console.log("NEW STATUS:", newStatus);

                    setOEM((prev) => ({
                      ...prev,
                      industrial_status: newStatus,
                    }));
                  }}
                >
                  <option value="active">{translate("active")}</option>
                  <option value="inactive">{translate("inactive")}</option>
                  <option value="incomplete">{translate("incomplete")}</option>
                </select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{translate("country") || "Country"}</Form.Label>
                <Form.Control
                  name="country"
                  value={oem.country}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{translate("city") || "City"}</Form.Label>
                <Form.Control
                  name="city"
                  value={oem.city}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  {translate("headquarters") || "Headquarters"}
                </Form.Label>
                <Form.Control
                  name="headquarters"
                  value={oem.headquarters}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{translate("address")}</Form.Label>
                <Form.Control
                  name="address"
                  value={oem.address}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{translate("website")}</Form.Label>
                <Form.Control
                  name="website"
                  value={oem.website}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {translate("cancel")}
        </Button>

        <Button variant="warning" onClick={handleUpdate}>
          {translate("update")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
